import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AppPermissionService } from '@core/services/app-permission.service';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { GameProviderHttpService } from '@core/services/game-provider-http.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'kt-promotion',
  templateUrl: './eligibility-setting.component.html',
  styleUrls: ['./eligibility-setting.component.scss']
})
export class EligibilitySettingComponent implements OnInit{
  form: FormGroup;
  // Bypass Turnover is gated behind its own permission (mirrors v1) — an operator without it sees
  // the toggle in its saved state but cannot change it.
  canBypassPromotionTurnover = false;
  private formSubscription: Subscription;
  private saveSubscription: Subscription;
  private permissionSubscription: Subscription;

  // Target Amount (Free Spin only) — AIO-71840
  isFreeSpin = false;
  targetTypes = [
    { id: 6, name: 'Turnover' },
    { id: 7, name: 'Winloss' },
  ];
  gameProvidersDropdownList: any[] = [];
  gameProvidersSelectedItems: any[] = [];

  constructor(
    @Optional() @Inject('initialData') private initialData: any,
    private eventEmitterService: EventEmitterService,
    private gameProviderHttpService: GameProviderHttpService,
    private appPermissionService: AppPermissionService,
  ) {
    this.initialData = initialData;
  }

  ngOnInit() {
    this.isFreeSpin = this.initialData?.promotion_type == 'Free Spin';
    this.permissionSubscription = this.appPermissionService.getAppPermissions().pipe(take(1)).subscribe(appPermissions => {
      this.canBypassPromotionTurnover = appPermissions.bypass_promotion_turnover;
    });
    this.formInit();
    this.eventEmitterService.onEligibilitySettingData(this.form.value);
    this.formSubscription = this.form.valueChanges.subscribe(values => {
      this.eventEmitterService.onEligibilitySettingData(values);
    });
    if (this.isFreeSpin) {
      this.loadGameProviders();
    }
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe();
    }
    if (this.permissionSubscription) {
      this.permissionSubscription.unsubscribe();
    }
  }

  onChangeToggle(event: any, controlName: string) {
    const isChecked = event.target.checked ? 1 : 0;
    this.form.patchValue({
      [controlName]: isChecked
    });

    // Auto Unlock and Unlock Upon Transfer Out are mutually exclusive — turning one on turns the other off.
    if (isChecked) {
      if (controlName === 'transfer_unlock') {
        this.form.patchValue({ auto_unlock: 0 });
      } else if (controlName === 'auto_unlock') {
        this.form.patchValue({ transfer_unlock: 0 });
      }
    }
  }

  private formInit() {
      // Coerce to Number: the API can return these flags as strings ("0"/"1"). A string "0" is truthy, and
      // formControlName assigns it straight to the checkbox's `checked`, so the toggle wrongly renders ON on edit.
      // Number("0") === 0 (falsy) fixes it. Use ?? so a genuine saved 0 isn't overridden by the default.
      // Free Spin: Unlock Upon Transfer Out / Auto Unlock are operator-editable and mutually
      // exclusive (see onChangeToggle), so a stored pair with both ON is normalized to
      // transfer-out wins. Other types keep them locked, so their stored values pass through.
      let transfer_unlock = Number(this.initialData?.transfer_unlock ?? 0);
      let auto_unlock = Number(this.initialData?.auto_unlock ?? 1);
      if (this.isFreeSpin && transfer_unlock === 1) {
        auto_unlock = 0;
      }
      let last_deposit = Number(this.initialData?.last_deposit ?? 0);
      // Free Spin: Auto Approve is always ON and locked in the UI.
      let auto_approve = this.isFreeSpin ? 1 : Number(this.initialData?.auto_approve ?? 1);
      let max_per_player = this.initialData?.max_per_player ?? 0; // 0 = unlimited
      let daily_max =  this.initialData?.daily_max || 1;

      this.form = new FormGroup({
        transfer_unlock: new FormControl(transfer_unlock),
        auto_unlock: new FormControl(auto_unlock),
        last_deposit: new FormControl(last_deposit),
        auto_approve: new FormControl(auto_approve),
        max_per_player: new FormControl(max_per_player),
        daily_max: new FormControl(daily_max, [Validators.required]),
    });

    // Target Amount — Free Spin only (added conditionally so it never leaks into PP / BTG payloads)
    if (this.isFreeSpin) {
      const target_type = this.initialData?.target_type ?? null; // 6: Turnover, 7: Winover
      const target_multiplier = this.initialData?.target_multiplier ?? null;
      const game_provider_ids = this.initialData?.target?.[0]?.game_provider_ids ?? [];

      this.form.addControl('target_type', new FormControl(target_type, Validators.required));
      // Transfer Amount (1 Included / 2 Excluded) — stored in promotion_game_providers.target_type per the v1 flow
      this.form.addControl('type', new FormControl(this.initialData?.type ?? null, Validators.required));
      this.form.addControl('target_multiplier', new FormControl(target_multiplier, Validators.required));
      this.form.addControl('game_provider_ids', new FormControl(game_provider_ids));
      this.form.addControl('target', new FormControl(game_provider_ids.length ? [{ game_provider_ids }] : []));
      // Game Categories is fixed to Slots; the SL category flag follows the Target Type.
      this.form.addControl('target_promotion_category_turnover', new FormControl(target_type == 6 ? 1 : 0));
      this.form.addControl('target_promotion_category_winloss', new FormControl(target_type == 7 ? 1 : 0));
      // Bypass Turnover — immediate unlock on claim (mirrors v1). Number() so a string "0"/"1" from the API is handled.
      this.form.addControl('bypass_turnover', new FormControl(Number(this.initialData?.bypass_turnover ?? 0)));
      // A promotion saved before this rule existed can hold Turnover + transfer_unlock ON. Normalize it
      // now so the disabled toggle never renders ON. Runs before ngOnInit subscribes to valueChanges,
      // so the first emit to the parent already carries the corrected pair.
      this.enforceTurnoverUnlockRule();
    }
  }

  // Target Amount handlers (Free Spin) — AIO-71840
  onTargetTypeChange() {
    const t = +this.form.value.target_type;
    this.form.patchValue({
      target_promotion_category_turnover: t === 6 ? 1 : 0,
      target_promotion_category_winloss: t === 7 ? 1 : 0,
    });
    this.enforceTurnoverUnlockRule();
  }

  // A Turnover target is measured by bet volume, so transferring out can never unlock it —
  // Unlock Upon Transfer Out applies to Winloss only. The template disables the toggle; this keeps
  // the value honest (a disabled toggle must never sit ON).
  get isTurnoverTarget(): boolean {
    return +this.form?.value?.target_type === 6;
  }

  get transferUnlockDisabled(): boolean {
    return !this.isFreeSpin || this.isTurnoverTarget;
  }

  // Force Unlock Upon Transfer Out off whenever the target becomes Turnover. Auto Unlock is restored
  // to ON because the two are mutually exclusive (see onChangeToggle) — leaving both OFF would hand
  // the operator a promotion with no unlock path at all.
  private enforceTurnoverUnlockRule() {
    if (!this.isTurnoverTarget || +this.form.value.transfer_unlock !== 1) {
      return;
    }

    this.form.patchValue({ transfer_unlock: 0, auto_unlock: 1 });
  }

  private originalType: any = null;
  private originalMultiplier: any = null;
  private originalTargetType: any = null;

  // Bypass Turnover = immediate unlock. The template hides the Target Amount inputs while it's ON, so
  // we save a complete valid default: Transfer Amount = Included (1), Target Multiplier = 0, and a
  // Target Type (kept if already chosen, else Turnover) with its matching category flag — otherwise the
  // step would be invalid on a required-but-hidden field. Turning it off restores the previous values.
  onBypassTurnoverChange(event: any) {
    // The template already blocks the input, but keep the handler honest: without the permission the
    // saved value must survive a stray change event untouched.
    if (!this.canBypassPromotionTurnover) {
      event.target.checked = this.bypassTurnover;
      return;
    }

    const checked = event.target.checked ? 1 : 0;
    this.form.patchValue({ bypass_turnover: checked });
    if (checked) {
      this.originalType = this.form.value.type;
      this.originalMultiplier = this.form.value.target_multiplier;
      this.originalTargetType = this.form.value.target_type;
      const targetType = this.form.value.target_type ?? 6; // 6 = Turnover (default)
      this.form.patchValue({
        type: 1,
        target_multiplier: 0,
        target_type: targetType,
        target_promotion_category_turnover: targetType == 6 ? 1 : 0,
        target_promotion_category_winloss: targetType == 7 ? 1 : 0,
      });
      this.enforceTurnoverUnlockRule(); // the default target above is Turnover
    } else {
      this.form.patchValue({
        type: this.originalType ?? null,
        target_multiplier: this.originalMultiplier ?? null,
        target_type: this.originalTargetType ?? null,
      });
    }
  }

  get bypassTurnover(): boolean {
    return +this.form?.value?.bypass_turnover === 1;
  }

  // v1-style formula from the combined target type (Target Type 6/7 + Transfer Amount 1/2 -> v1 type 1-4)
  get targetAmountFormula(): string {
    const targetType = +this.form?.value?.target_type;
    const transfer = +this.form?.value?.type;
    if (!targetType || !transfer) return '';
    const t = (targetType === 6 ? 1 : 2) + (transfer === 2 ? 2 : 0);
    switch (t) {
      case 1: return 'Target Amount = (Transfer Amount + Bonus) x Target Multiplier';
      case 2: return 'Target Amount = (Transfer Amount + Bonus) + [(Transfer Amount + Bonus) x Target Multiplier]';
      case 3: return 'Target Amount = Transfer Amount + (Bonus x Target Multiplier)';
      case 4: return 'Target Amount = (Transfer Amount + Bonus) + [(Bonus x Target Multiplier)]';
      default: return '';
    }
  }

  // Read-only label for the locked Free Spin Game Provider (mirrors Basic Setting).
  // The label comes from the provider list, so the field is blank until that request lands.
  isGameProvidersLoading = false;

  get freeSpinProviderLabel(): string {
    return this.gameProvidersSelectedItems?.[0]?.labelKey ?? '';
  }

  private loadGameProviders() {
    this.isGameProvidersLoading = true;
    this.gameProviderHttpService.getGameProviders().subscribe({
      next: (res: any[]) => {
        this.gameProvidersDropdownList = (res || []).map((p: any) => ({ ...p, labelKey: p.code + ' - ' + p.name }));
        // Free Spin: the target Game Provider is locked to the Free Spin Game Provider (Basic Setting) —
        // not operator-chosen. Mirror it here; the template disables the dropdown.
        const fsProviderId = this.initialData?.free_spin_game_provider_id ?? null;
        if (fsProviderId) {
          this.gameProvidersSelectedItems = this.gameProvidersDropdownList.filter((p: any) => p.id == fsProviderId);
          this.form.patchValue({
            game_provider_ids: [fsProviderId],
            target: [{ game_provider_ids: [fsProviderId] }],
          });
        } else {
          const ids = this.form.value.game_provider_ids || [];
          this.gameProvidersSelectedItems = this.gameProvidersDropdownList.filter((p: any) => ids.includes(p.id));
        }
        this.isGameProvidersLoading = false;
      },
      // On failure the global ApiErrorInterceptor shows the error dialog; clear stale options.
      error: () => {
        this.gameProvidersDropdownList = [];
        this.gameProvidersSelectedItems = [];
        this.isGameProvidersLoading = false;
      },
    });
  }

  onNumberKeydown(event: KeyboardEvent) {
    if (event.key === '-' || event.key === 'e') {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData?.getData('text');

    if (pastedText && (pastedText.includes('-') || pastedText.toLowerCase().includes('e'))) {
      event.preventDefault();
    }
  }
}
